import { Field, FieldProps, FormikProps, useFormikContext } from 'formik';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker, FormikPoolTimePicker } from '@synergy/components-next';
import { PaloField } from '@synergy/components-palo';
import { fields } from '@synergy/utils';
import dayjs from 'dayjs';

import { DemandSteps } from '../models/demand-steps';
import { OverviewFormValues } from '../models/overview-form-values';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

const fieldNames = fields<OverviewFormValues>();

const useReadonly = () => useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Overview);

/** Date and time of one moment, side by side in one cell. */
const DateAndTime = ({ name, readonly, disablePast }: { name: string; readonly: boolean; disablePast?: boolean }) => (
    <Stack direction="row" gap="8px">
        <FormikPoolDatePicker name={name} disablePast={disablePast} readOnly={readonly} setTimeToMidday={false} />
        <FormikPoolTimePicker name={name} readOnly={readonly} />
    </Stack>
);

/** When the survey starts: now, or at a date and time (head grid, area `start`). */
export const DemandStartField = () => {
    const formik = useFormikContext<OverviewFormValues>();
    const readonly = useReadonly();

    const handleStartNow = (value: boolean, form: FormikProps<OverviewFormValues>) => {
        form.setFieldValue(fieldNames.isScheduled, value);
        form.setFieldValue(fieldNames.startDate, dayjs().add(2, 'hour'));
    };

    return (
        <PaloField
            label={
                <>
                    <FormattedMessage id="app.label.startDate" /> *
                </>
            }
            name={[fieldNames.isScheduled, fieldNames.startDate]}
            area="start"
        >
            <Stack direction="row" alignItems="center" gap="10px">
                <Field name={fieldNames.isScheduled}>
                    {({ field, form }: FieldProps<boolean>) => (
                        <FormControlLabel
                            label={<FormattedMessage id="app.demands.detail.overview.startNow" />}
                            sx={{ mr: 0, flexShrink: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px' } }}
                            control={
                                <Switch
                                    name={field.name}
                                    checked={field.value}
                                    onChange={() => handleStartNow(!field.value, form)}
                                    color="primary"
                                    size="small"
                                    disabled={readonly}
                                />
                            }
                        />
                    )}
                </Field>
                {!formik.values.isScheduled && (
                    <DateAndTime name={fieldNames.startDate} readonly={readonly} disablePast />
                )}
            </Stack>
        </PaloField>
    );
};

/** When the survey ends (head grid, area `end`). */
export const DemandEndField = () => {
    const readonly = useReadonly();

    return (
        <PaloField
            label={
                <>
                    <FormattedMessage id="app.label.endDate" /> *
                </>
            }
            name={fieldNames.endDate}
            area="end"
        >
            <DateAndTime name={fieldNames.endDate} readonly={readonly} disablePast />
        </PaloField>
    );
};
